import * as THREE from 'three';
import { Quat } from '@/core/math/Quat';
import { Vec3 } from '@/core/math/Vec3';
import { CelestialBody } from '@/universe/CelestialBody';
import { clamp, clamp01 } from '@/core/math/scalar';
import { hashString, toUnit } from '@/core/math/hash';
import {
  GLSL_CLOUD_FIELD,
  GLSL_CONSTANTS,
  GLSL_DITHER,
  GLSL_NOISE,
  GLSL_RING_DENSITY,
} from './shaders/common.glsl';
import {
  DENSITY_SCALE_MAX,
  DENSITY_SCALE_MIN,
  DENSITY_SCALE_NOMINAL,
  MIE_SCALE_HEIGHT_FRACTION,
  MIN_SCALE_HEIGHT,
  SUN_COLOR,
  SUN_INTENSITY_DEFAULT,
} from './shaders/atmosphereDefaults';
import {
  GLSL_SHADOW_CASCADE,
  GLSL_SHADOW_CASCADE_UNIFORMS,
  ShadowCascadeUniforms,
  createShadowCascadeUniforms,
  setShadowCascadeUniforms,
  setShadowFadeUniform,
} from './shaders/shadowCascade';
import { ScreenSpaceReflection } from './ScreenSpaceReflection';
import { ShadowCascade } from './LayeredRenderer';

/**
 * Octave count for the isotropic chop field, `waveHeight`. Shared between the
 * `uQuality` clamp below (TypeScript) and the vertex shader's `for` loop
 * bound (GLSL ES needs a compile-time constant there, not `uQuality` itself)
 * — a single source of truth so raising one without the other cannot
 * silently cap the quality slider below what it claims to offer.
 */
const MAX_WAVE_OCTAVES = 3;

/**
 * Cameras at or above this altitude stop running SSR. The local ocean patch is
 * only used below `handoverAltitude`, and beyond this range the reflected
 * geometry is so far that the procedural sky reflection is indistinguishable —
 * gating saves the whole capture render (and the shader's march) up there.
 */
const SSR_MAX_ALTITUDE_M = 25000;

// Builds the local tangent directions of two planet-fixed coordinate axes at
// an arbitrary point on the sea sphere. The axes rotate with the body, never
// with the camera; only their tangent projections vary over the curved sea.
const WAVE_BASIS_GLSL = /* glsl */ `
void planetWaveBasis(vec3 up, out vec3 tangentX, out vec3 tangentY) {
  vec3 projectedX = uWaveAxisX - up * dot(uWaveAxisX, up);
  vec3 projectedY = uWaveAxisY - up * dot(uWaveAxisY, up);
  if (dot(projectedX, projectedX) > 1e-8) {
    tangentX = normalize(projectedX);
    projectedY -= tangentX * dot(projectedY, tangentX);
    tangentY = dot(projectedY, projectedY) > 1e-8
      ? normalize(projectedY)
      : normalize(cross(up, tangentX));
  } else {
    tangentY = normalize(projectedY);
    tangentX = normalize(cross(tangentY, up));
  }
}
`;

/**
 * Ocean rendering with two representations and a continuous handover.
 *
 *   Local  A radial grid centred beneath the camera, curved onto the planet's
 *          sphere, displaced by a directional Gerstner swell train plus an
 *          isotropic fbm chop riding on top. Used below the handover
 *          altitude, where the horizon must be genuinely curved and waves
 *          must have real relief.
 *
 *   Orbital A single sphere at the sea-level radius with the wave detail faded
 *          out. Used above the handover altitude, where individual waves are
 *          smaller than a pixel and only the shape of the ocean basin matters.
 *
 * Both use the same shader and the same colour parameters, and the wave
 * amplitude fades to zero across the handover band, so the switch is not
 * visible. A radial grid is used for the local patch rather than a square one
 * because it puts vertex density where it matters (near the viewer) and its
 * outer edge is a circle, which matches the horizon.
 */

const VERTEX = /* glsl */ `
precision highp float;

${GLSL_CONSTANTS}
${GLSL_NOISE}

varying vec3 vRenderPosition;
varying vec3 vPlanetPos;
varying vec3 vNormal;
varying float vWaveHeight;
varying float vBreaking;
varying float vDistance;
varying float vWaveLod;

uniform vec3 uPlanetCentre;
uniform float uSeaRadius;
uniform float uTime;
uniform float uWaveAmplitude;
uniform float uWaveScale;
uniform float uSurfaceGravity;
uniform float uWindAngle;
uniform vec3 uWaveAxisX;
uniform vec3 uWaveAxisY;
uniform int uQuality;
uniform float uWaveGeometryFadeStart;
uniform float uWaveGeometryFadeEnd;

${WAVE_BASIS_GLSL}

const float TOLERANCE = 1e-4; // min denominator for the 'up' normalisation

// Chop scroll follows the same real-time deep-water dispersion as the swell.
// For feature wavelength L=uWaveScale/freq, phase speed is
// sqrt(g*L/(2*pi)). Since q is measured in units of uWaveScale, the equivalent
// q-space rate is sqrt(g*freq/(2*pi*uWaveScale)). Shorter ripples therefore
// move more slowly in metres/second but oscillate more frequently.
// Z-axis scroll relative to X, so the octaves still drift apart over time
// (the "don't stay visually locked together" behaviour) without one racing
// another.
const float CHOP_DRIFT_FACTOR = 0.6;
const float CHOP_GRADIENT_EPS = 0.02;   // finite-difference step for the chop normal gradient
const float CHOP_AMPLITUDE_FALLOFF = 0.55; // per-octave amplitude multiplier (fine octaves quieter)
const float CHOP_FREQUENCY_GROWTH = 1.9;  // per-octave frequency multiplier (was 2.4: the finest octave was sub-10 m grain)

// Sum of up to MAX_WAVE_OCTAVES travelling wave trains, phase-shifted so they
// never resonate into an obvious repeating pattern. This is an isotropic
// height field — no travel direction, no crest/trough asymmetry — which is
// fine for short wind-chop but reads as a bumpy blanket rather than a sea on
// its own. The directional swell train below carries the rest of the
// amplitude budget as genuine peaked displacement; this stays as the fine
// detail riding on top.
float waveHeight(vec3 p, out vec3 gradient) {
  if (uQuality <= 0) {
    gradient = vec3(0.0);
    return 0.0;
  }
  vec3 q = p / uWaveScale;
  float h = 0.0;
  gradient = vec3(0.0);

  float amp = 1.0;
  float freq = 1.0;
  for (int i = 0; i < ${MAX_WAVE_OCTAVES}; i++) {
    if (i >= uQuality) break;
    float phaseSpeed = sqrt(
      max(uSurfaceGravity, 0.05) * freq
        / (2.0 * PI * max(uWaveScale, 1.0))
    );
    vec3 s = q * freq + vec3(
      uTime * phaseSpeed, 0.0,
      uTime * phaseSpeed * CHOP_DRIFT_FACTOR);
    float n = fbm(s, 3) - 0.5;
    h += n * amp;
    // Finite-difference gradient for the normal.
    float e = CHOP_GRADIENT_EPS;
    gradient.x += (fbm(s + vec3(e, 0.0, 0.0), 2) - fbm(s - vec3(e, 0.0, 0.0), 2)) * amp / (2.0 * e);
    gradient.z += (fbm(s + vec3(0.0, 0.0, e), 2) - fbm(s - vec3(0.0, 0.0, e), 2)) * amp / (2.0 * e);
    amp *= CHOP_AMPLITUDE_FALLOFF;
    freq *= CHOP_FREQUENCY_GROWTH;
  }
  return h;
}

// Gerstner steepness (Q) is capped well short of the point a trochoid folds
// over on itself (Q*k*amplitude -> 1): unconditionally safe
// self-intersection-wise, not just for the amplitude range this project's
// worlds currently produce.
const float GERSTNER_STEEPNESS_CAP = 0.85;
const float GERSTNER_STEEPNESS_RATIO = 0.55;
const float GERSTNER_STEEPNESS_EPS = 1e-4;
// One large-scale directional swell train, in closed (Gerstner/trochoidal)
// form rather than a height field: it displaces each vertex along the wave
// direction as well as up and down, which is what pulls crests into a point
// and spreads troughs out. A pure height field cannot produce that asymmetry
// no matter how it is shaped, which is most of why the sea read as an
// undirected bumpy noise blanket rather than real waves.
//
// Angular frequency follows the real deep-water dispersion relation
// (w = sqrt(g*k), phase speed sqrt(g/k)) in simulation seconds. Long swells
// travel faster across the surface while short waves oscillate more often.
void gerstnerWave(vec2 uv, float amplitude, float wavelength, float angle, float phaseOffset, float t,
                  out vec2 horizontal, out float vertical, out vec2 slope, out float breaking) {
  vec2 dir = vec2(cos(angle), sin(angle));
  float k = (2.0 * PI) / max(wavelength, 1.0);
  float steepness = min(GERSTNER_STEEPNESS_CAP, GERSTNER_STEEPNESS_RATIO / max(k * amplitude, GERSTNER_STEEPNESS_EPS));
  float w = sqrt(max(uSurfaceGravity, 0.05) * k);
  float phase = dot(dir, uv) * k - t * w + phaseOffset;
  float c = cos(phase);
  float s = sin(phase);

  horizontal = dir * steepness * amplitude * c;
  vertical = amplitude * s;
  slope = dir * k * amplitude * c;
  breaking = smoothstep(0.45, 0.92, s) * clamp(k * amplitude * steepness * 2.5, 0.0, 1.0);
}

// |up.y| above which the tangent basis switches its reference axis, to avoid
// building a degenerate (near-parallel) cross product near the poles.
const float REF_AXIS_THRESHOLD = 0.9;

// Amplitude budget split between the isotropic chop field and the
// directional swell train (see the "Chop now carries..." comment below): the
// swell carries the larger share so the sea reads as a legible rolling shape
// rather than being buried under high-frequency noise.
const float WAVE0_AMPLITUDE = 0.52;
const float WAVE1_AMPLITUDE = 0.24;
const float WAVE2_AMPLITUDE = 0.14;
const float WAVE3_AMPLITUDE = 0.07;
// A real wind sea is a narrow directional spectrum. The old offsets spread
// the four trains over more than ninety degrees; their visible crests crossed
// into a regular diamond lattice that read as a texture laid over the water.
// Keep enough angular spread to avoid a synthetic single sine wave, but make
// all components belong to the same prevailing swell.
const float WAVE1_DIRECTION_OFFSET = 0.14;
const float WAVE2_DIRECTION_OFFSET = -0.21;
const float WAVE3_DIRECTION_OFFSET = 0.29;
// Swell wavelength relative to the chop's own uWaveScale, so the swell reads
// as a distinctly larger-scale feature than the chop riding on it.
const float SWELL_WAVELENGTH_SCALE = 1.0;
// Normal perturbation strength: how strongly the chop gradient and the
// swell's analytic slope each tilt the surface normal away from the exact
// radial 'up'.
const float CHOP_NORMAL_GRADIENT_SCALE = 0.0;
const float SWELL_NORMAL_SLOPE_SCALE = 0.72;
// The camera-centred radial mesh grows to kilometre-sized triangles near the
// horizon. Geometry waves cannot be represented there without exposing those
// triangles as long wedges, so displacement hands over to a smooth analytic
// sea before the mesh becomes undersampled. Nearby water keeps the full sea
// state; the horizon keeps reflection/colour but no fake kilometre-wide waves.
const float WAVE_GEOMETRY_FADE_START = 6000.0;
const float WAVE_GEOMETRY_FADE_END = 26000.0;

void main() {
  vec4 world = modelMatrix * vec4(position, 1.0);
  vec3 planetRelative = world.xyz - uPlanetCentre;
  float r = length(planetRelative);
  vec3 up = planetRelative / max(r, TOLERANCE);

  // Project the vertex exactly onto the sea-level sphere, so a flat grid
  // becomes a curved ocean surface with a correct horizon.
  vec3 onSphere = uPlanetCentre + up * uSeaRadius;
  vec3 base = onSphere - uPlanetCentre;

  // Local tangent frame, needed before the wave functions: the swell's
  // direction and its analytic slope are both 2D quantities defined in the
  // plane perpendicular to 'up', not in the mesh's own object-space axes
  // (which only line up with that plane for the local patch, not the
  // orbital sphere).
  vec3 tangent;
  vec3 bitangent;
  planetWaveBasis(up, tangent, bitangent);
  vec2 uv = vec2(dot(base, uWaveAxisX), dot(base, uWaveAxisY));

  // Chop now carries a reduced share of the total amplitude budget; the
  // swell carries the majority of it as directional, peaked displacement —
  // weighted toward the swell specifically so the large legible rolling
  // shape reads clearly instead of being buried under high-frequency noise,
  // which is what "doesn't look like waves" actually meant: the old all-chop
  // field had motion but no readable shape.
  vec2 horizontal = vec2(0.0);
  vec2 slope = vec2(0.0);
  float h = 0.0;
  float breaking = 0.0;
  vec2 wh;
  float wv;
  vec2 ws;
  float wb;

  if (uQuality > 0) {
    gerstnerWave(uv, uWaveAmplitude * WAVE0_AMPLITUDE, uWaveScale, uWindAngle, 0.0, uTime, wh, wv, ws, wb);
    horizontal += wh; h += wv; slope += ws; breaking = max(breaking, wb);
  }
  if (uQuality > 1) {
    gerstnerWave(uv, uWaveAmplitude * WAVE1_AMPLITUDE, uWaveScale * 0.53, uWindAngle + WAVE1_DIRECTION_OFFSET, 2.1, uTime, wh, wv, ws, wb);
    horizontal += wh; h += wv; slope += ws; breaking = max(breaking, wb);
    gerstnerWave(uv, uWaveAmplitude * WAVE2_AMPLITUDE, uWaveScale * 0.29, uWindAngle + WAVE2_DIRECTION_OFFSET, 4.4, uTime, wh, wv, ws, wb);
    horizontal += wh; h += wv; slope += ws; breaking = max(breaking, wb);
  }
  if (uQuality > 2) {
    gerstnerWave(uv, uWaveAmplitude * WAVE3_AMPLITUDE, uWaveScale * 0.16, uWindAngle + WAVE3_DIRECTION_OFFSET, 1.3, uTime, wh, wv, ws, wb);
    horizontal += wh; h += wv; slope += ws; breaking = max(breaking, wb);
  }

  float baseDistance = length(onSphere - cameraPosition);
  float waveLod = 1.0 - smoothstep(WAVE_GEOMETRY_FADE_START, WAVE_GEOMETRY_FADE_END, baseDistance);
  horizontal *= waveLod;
  h *= waveLod;
  slope *= waveLod;
  breaking *= waveLod;

  vWaveHeight = h;
  vBreaking = breaking;
  vWaveLod = waveLod;

  vec3 displaced = onSphere + up * h + tangent * horizontal.x + bitangent * horizontal.y;
  vRenderPosition = displaced;
  vPlanetPos = displaced - uPlanetCentre;
  vDistance = length(displaced - cameraPosition);

  // Perturb the normal by the chop's finite-difference gradient (at its new
  // reduced weight) and the swell's own analytic slope, in the same local
  // tangent plane.
  vNormal = normalize(up
    - tangent * slope.x * SWELL_NORMAL_SLOPE_SCALE
    - bitangent * slope.y * SWELL_NORMAL_SLOPE_SCALE);

  gl_Position = projectionMatrix * viewMatrix * vec4(displaced, 1.0);
}
`;

const FRAGMENT = /* glsl */ `
precision highp float;

${GLSL_CONSTANTS}
${GLSL_NOISE}
${GLSL_RING_DENSITY}
${GLSL_CLOUD_FIELD}

varying vec3 vRenderPosition;
varying vec3 vPlanetPos;
varying vec3 vNormal;
varying float vWaveHeight;
varying float vBreaking;
varying float vDistance;
varying float vWaveLod;

uniform vec3 uSunDirection;
uniform vec3 uSunColor;
uniform float uSunIntensity;
uniform vec3 uShallowColor;
uniform vec3 uDeepColor;
uniform vec3 uSkyColor;
uniform vec3 uPlanetCentre;
uniform float uTime;
uniform float uSeaRadius;
uniform float uWaveAmplitudeBase;
uniform float uWaveAmplitude;
uniform float uWaveScale;
uniform float uSurfaceGravity;
uniform float uWindAngle;
uniform vec3 uWaveAxisX;
uniform vec3 uWaveAxisY;
uniform int uQuality;
uniform float uPassMin;
uniform float uPassMax;
uniform vec3 uRingNormal;
uniform float uRingInnerRadius;
uniform float uRingOuterRadius;
uniform float uRingOpacity;
uniform vec3 uRingColor;
uniform vec3 uRingAtmosphereTau;
uniform float uCloudCoverage;
uniform float uCloudRadius;
uniform float uCloudDrift;
/** Cross-fade with the body's distant impostor. See GLSL_DITHER. */
uniform float uSurfaceFade;

// Terrain's cascaded self-shadow map, sampled here too so water sitting in a
// ridge's or cliff's shadow doesn't stay lit while the sand beside it goes
// dark. The uniforms, bias/PCF tuning and lookup are the same ones the
// terrain surface uses — see shaders/shadowCascade.ts.
${GLSL_SHADOW_CASCADE_UNIFORMS}
${GLSL_DITHER}

uniform float uSsrEnabled;
uniform sampler2D uSsrColor;
uniform sampler2D uSsrDepth;
uniform mat4 uSsrProjection;
uniform mat4 uSsrProjectionInverse;
uniform mat4 uSsrViewMatrix;
uniform float uSsrFar;
uniform vec2 uSsrTexelSize;

${WAVE_BASIS_GLSL}

// Fresnel: reflectance at normal incidence and the power shaping how fast it
// rises toward FRESNEL_GRAZING (nearly a mirror) at grazing angles.
const float FRESNEL_NORMAL = 0.02;
const float FRESNEL_GRAZING = 0.98;
const float FRESNEL_POWER = 5.0;

// Same cap TerrainMaterial uses for its cloud shadow: the deck is thin and
// never opaque, so full darkening would read as a hole cut in the sea.
const float CLOUD_SHADOW_STRENGTH = 0.68;

// Underwater view. The underside of a water surface is nearly transparent
// looking straight up and approaches a mirror at grazing angles (total
// internal reflection), so the same Fresnel curve drives opacity below the
// surface, on top of the reflection it already drives above it.
const float SURFACE_OPACITY_NADIR = 0.96; // barely see-through straight down above water
const float UNDERWATER_OPACITY_NADIR = 0.12; // see the sky through it, looking up
const float UNDERWATER_OPACITY_GRAZING = 0.95;
const float UNDERWATER_ABSORPTION = 0.02; // per-metre light loss with camera depth
const float UNDERWATER_COLOR_FLOOR = 0.15; // deep water never goes fully black

// uSunIntensity is set from the body's distance to the star, not from the
// hour, so it is still nonzero at midnight; this gate gives water its own
// day/night falloff so grazing-angle Fresnel reflection cannot light the sea
// at night regardless. Range is dot(up, sunDirection).
const float DAYLIGHT_START = -0.18;
const float DAYLIGHT_END = 0.12;

// Body colour contrast: how strongly wave height brightens the shallow tint
// over the deep one, so the swell's shape reads in flat colour and not only
// in specular highlights.
// Height should not paint every crest as a separate pale stripe. Most of a
// water wave is read through its normal/reflection; this only keeps a very
// gentle crest-to-trough absorption change.
const float BODY_COLOR_WAVE_CONTRAST = 0.06;
// Diffuse term composition: a small ambient-ish floor (so night-side glow
// under 'daylight' isn't pure black even before the day/night gate) plus a
// direct Lambertian contribution.
const float BODY_AMBIENT_FLOOR = 0.035;
const float BODY_DIFFUSE_WEIGHT = 0.20;

// Sky reflection gradient: a warm near-white horizon blending down from the
// body's own haze/sky colour, darkening toward the zenith.
const vec3 SKY_HORIZON_TINT = vec3(0.82, 0.88, 0.92);
const float SKY_HORIZON_MIX = 0.28;
const float SKY_ZENITH_DARKEN = 0.52;
const float SUN_GLOW_POWER = 48.0;   // tightness of the reflected solar glow
const float SUN_GLOW_STRENGTH = 0.28;
const float SKY_REFLECTION_INTENSITY = 0.34; // sky reflection brightness relative to uSunIntensity
const float SKY_REFLECTION_FRESNEL_GAIN = 0.72; // how much Fresnel gates the reflection blend

// Multi-point sparkle field: perturbs the specular direction with fine
// per-fragment noise so highlights scatter across the whole sunlit surface
// instead of one soft sun-aligned blob.
const float SPARKLE_COORD_SCALE = 0.35;
const float SPARKLE_TIME_SCROLL = 0.6;
const float SPARKLE_FOOTPRINT_SCALE = 3.0;  // screen-space-derivative fade rate, same idea as TerrainMaterial's footprint fade
const float SPARKLE_FOOTPRINT_MIN = 1e-6;
const vec3 SPARKLE_NOISE_OFFSET = vec3(17.3, 4.1, 9.7); // decorrelates the 2nd jitter channel from the 1st
const float SPARKLE_JITTER_STRENGTH = 0.10;

// Primary specular highlight, Toksvig-corrected against normal
// undersampling from the local patch's growing far-field triangles (see
// buildRadialGrid's exponential ring spacing).
const float SPECULAR_SHININESS = 380.0;
const float TOKSVIG_VARIANCE_GAIN = 0.25;
const float SPECULAR_STRENGTH = 0.65;

// Sparkle specular: a moderate exponent (not SPECULAR_SHININESS — this is
// meant to scatter, not spike) and its own strength, faded by the same
// sparkleFade that already zeroes the jitter at distance. Strength and
// exponent were knocked down together (1.3/160 -> 0.8/260) so the handful of
// surviving glints read as discrete flashes rather than an all-over film of
// per-fragment mottle that made the surface read as noise instead of a sea.
const float SPARKLE_SHININESS = 260.0;
const float SPARKLE_STRENGTH = 0.22;

// Open-water whitecaps are fine detail and may disappear once they are smaller
// than a pixel. Shore break is different: it is a broad geographic feature and
// must continue to the visible horizon. Its range is derived from the opaque
// capture below, rather than sharing this deliberately short whitecap fade.
const float WHITECAP_DISTANCE_START = 2500.0;
const float WHITECAP_DISTANCE_END = 20000.0;
const float SHORE_CAPTURE_FADE_START = 0.72;
const float SHORE_CAPTURE_FADE_END = 0.96;
const vec3 FOAM_COLOR = vec3(0.85, 0.9, 0.95);
const float FOAM_INTENSITY = 0.72;
const float FOAM_BLEND_STRENGTH = 0.78;
const float SHORE_DEPTH_MIN = 0.35;
const float SHORE_DEPTH_BASE = 8.0;
const float SHORE_DEPTH_WAVE_GAIN = 16.0;
const float SHORE_NOISE_SCALE = 0.018;
const float WHITECAP_NOISE_SCALE = 0.0035;

// Exact sea-sphere intersection for optical shading. The radial mesh only
// supplies coverage and nearby silhouette displacement; projecting its planar
// triangles radially makes the wave coordinates piecewise and can reintroduce
// the fan pattern through derivatives. This factored quadratic is stable at
// planetary radii and gives every pixel the same smooth base sphere.
float seaSphereDistance(vec3 origin, vec3 dir) {
  float b = dot(origin, dir);
  float len = length(origin);
  float c = (len - uSeaRadius) * (len + uSeaRadius);
  float d = b * b - c;
  if (d < 0.0) return -1.0;
  float sd = sqrt(d);
  float nearHit = -b - sd;
  if (nearHit > 0.0) return nearHit;
  float farHit = -b + sd;
  return farHit > 0.0 ? farHit : -1.0;
}

// Per-pixel wave evaluation. The mesh still carries a small amount of nearby
// displacement for silhouette/parallax, but its growing radial triangles must
// never define the optical normal: doing that turns each far triangle into a
// kilometre-wide reflective wedge. Re-evaluating the compact directional
// spectrum here makes reflection continuous at pixel resolution.
void opticalWave(
  vec2 uv,
  float amplitude,
  float wavelength,
  float angle,
  float phaseOffset,
  out float height,
  out vec2 slope,
  out float breaking
) {
  vec2 dir = vec2(cos(angle), sin(angle));
  float k = 6.28318530718 / max(wavelength, 1.0);
  float w = sqrt(max(uSurfaceGravity, 0.05) * k);
  float phase = dot(dir, uv) * k - uTime * w + phaseOffset;
  // Analytically band-limit each train in screen space. At a grazing view a
  // 7–40 m wave can cross several periods inside one pixel; sampling the raw
  // sine then aliases into the concentric zebra/moiré pattern previously seen
  // across the whole sea. Fade the train before it approaches that limit.
  float phaseFootprint = max(abs(dFdx(phase)), abs(dFdy(phase)));
  float waveSample = 1.0 - smoothstep(0.45, 1.35, phaseFootprint);
  float s = sin(phase);
  float c = cos(phase);
  height = amplitude * s * waveSample;
  slope = dir * k * amplitude * c * waveSample;
  float steepness = min(0.85, 0.55 / max(k * amplitude, 1e-4));
  breaking = smoothstep(0.45, 0.92, s)
    * clamp(k * amplitude * steepness * 2.5, 0.0, 1.0)
    * waveSample;
}

// --- Screen-space reflections ------------------------------------------------
//
// The reflected ray is marched in view space against the SSR capture's depth
// buffer, reconstructed to linear view-space distance so one camera can span
// near→mid without the partition bookkeeping the main passes need. The march
// starts one step off the water surface so the capture's background depth (the
// sea floor at the water pixel, from which the ocean was hidden) is never
// mistaken for a hit; the reflected ray points up into the sky hemisphere, so
// over open water it walks to the horizon, hits the depth's far plane and
// falls back to the procedural sky reflection below.
const int SSR_STEPS = 24;
const int SSR_BINARY_STEPS = 5;
const float SSR_THICKNESS = 0.4;   // metres, flat floor against depth quantisation
const float SSR_THICKNESS_FACTOR = 0.012; // grows with ray distance to match depth precision loss
const float SSR_FAR_PLANE = 0.999; // treat depth at/behind this as "no geometry" -> sky fallback
const float SSR_MIN_STEP = 0.5;    // metres, floor under the adaptive step
const float SSR_REFLECTION_STRENGTH = 0.72; // how hard a confirmed hit overrides the procedural reflection

bool ssrRayMarch(vec3 viewPos, vec3 normalView, out vec3 hitColor, out float hitConfidence) {
  hitColor = vec3(0.0);
  hitConfidence = 0.0;
  if (uSsrEnabled <= 0.5) return false;

  // viewPos already points camera -> fragment. Reflecting -viewPos sent the
  // ray back through the water/sea floor and was the main source of the large
  // triangular false hits. The outgoing mirror ray is reflect(camera->surface).
  vec3 incident = normalize(viewPos);
  vec3 R = reflect(incident, normalView);

  float step = max(length(viewPos) / float(SSR_STEPS), SSR_MIN_STEP);
  // Skip the start pixel: its captured depth is the sea floor along the
  // *incident* ray, closer than the water, which would read as an instant hit.
  vec3 low = viewPos + R * step;

  for (int i = 0; i < SSR_STEPS; i++) {
    vec3 high = low + R * step;
    vec4 clip = uSsrProjection * vec4(high, 1.0);
    vec3 ndc = clip.xyz / clip.w;
    if (ndc.x < -1.0 || ndc.x > 1.0 || ndc.y < -1.0 || ndc.y > 1.0) break;
    vec2 uv = ndc.xy * 0.5 + 0.5;
    float rawDepth = texture2D(uSsrDepth, uv).r;
    if (rawDepth >= SSR_FAR_PLANE) break;

    vec4 invClip = vec4(ndc.xy, rawDepth * 2.0 - 1.0, 1.0);
    vec4 vs = uSsrProjectionInverse * invClip;
    vec3 surf = vs.xyz / vs.w;
    float thickness = SSR_THICKNESS + length(high) * SSR_THICKNESS_FACTOR;
    // Hardware depth is measured along the view axis, not by Euclidean range.
    // Comparing vector lengths makes two unrelated off-axis points look like a
    // crossing and produces screen-sized wedges on oblique water.
    if (-high.z >= -surf.z - thickness) {
      // Crossed the surface. Bisect between low and high to pin the hit.
      for (int b = 0; b < SSR_BINARY_STEPS; b++) {
        vec3 mid = (low + high) * 0.5;
        vec4 mc = uSsrProjection * vec4(mid, 1.0);
        vec3 mndc = mc.xyz / mc.w;
        if (mndc.x < -1.0 || mndc.x > 1.0 || mndc.y < -1.0 || mndc.y > 1.0) break;
        vec2 muv = mndc.xy * 0.5 + 0.5;
        float mraw = texture2D(uSsrDepth, muv).r;
        if (mraw >= SSR_FAR_PLANE) break;
        vec4 minv = vec4(mndc.xy, mraw * 2.0 - 1.0, 1.0);
        vec4 mvs = uSsrProjectionInverse * minv;
        vec3 msurf = mvs.xyz / mvs.w;
        if (-mid.z >= -msurf.z - thickness) high = mid;
        else low = mid;
      }
      vec3 hitView = (low + high) * 0.5;
      vec4 hc = uSsrProjection * vec4(hitView, 1.0);
      vec3 hndc = hc.xyz / hc.w;
      vec2 huv = hndc.xy * 0.5 + 0.5;
      // The capture deliberately contains the sea floor so it can also drive
      // shoreline foam. It must not be accepted as reflected geometry: a ray
      // travelling above the surface otherwise locks onto submerged terrain
      // and produces the large pale islands visible in the old SSR.
      float hitRawDepth = texture2D(uSsrDepth, huv).r;
      vec4 hitInvClip = vec4(hndc.xy, hitRawDepth * 2.0 - 1.0, 1.0);
      vec4 hitSurface4 = uSsrProjectionInverse * hitInvClip;
      vec3 hitSurface = hitSurface4.xyz / hitSurface4.w;
      vec3 centreView = (uSsrViewMatrix * vec4(uPlanetCentre, 1.0)).xyz;
      if (length(hitSurface - centreView) < uSeaRadius + 0.25) {
        low = high;
        continue;
      }
      hitColor = texture2D(uSsrColor, huv).rgb;
      vec2 edgeDistance = min(huv, vec2(1.0) - huv);
      float depthAgreement = 1.0 - smoothstep(thickness, thickness * 3.0, abs(hitView.z - hitSurface.z));
      float travel = length(hitView - viewPos);
      float travelConfidence = 1.0 - smoothstep(30000.0, 90000.0, travel);
      hitConfidence = smoothstep(0.0, 0.075, min(edgeDistance.x, edgeDistance.y))
        * depthAgreement * travelConfidence;
      if (hitConfidence <= 0.01) {
        low = high;
        continue;
      }
      return true;
    }
    low = high;
  }
  return false;
}

// Vertical water depth beneath this fragment from the same opaque capture SSR
// uses. This is a stable screen-space proxy for distance to shore: breakers
// appear where the sea floor rises into the wave base, and the width of that
// band grows with the authored sea state.
float capturedWaterDepthAtUv(vec2 uv) {
  if (uv.x <= 0.0 || uv.x >= 1.0 || uv.y <= 0.0 || uv.y >= 1.0) return 1e6;
  float rawDepth = texture2D(uSsrDepth, uv).r;
  if (rawDepth >= SSR_FAR_PLANE) return 1e6;
  vec2 ndc = uv * 2.0 - 1.0;
  vec4 invClip = vec4(ndc.xy, rawDepth * 2.0 - 1.0, 1.0);
  vec4 floorView4 = uSsrProjectionInverse * invClip;
  vec3 floorView = floorView4.xyz / floorView4.w;
  vec3 centreView = (uSsrViewMatrix * vec4(uPlanetCentre, 1.0)).xyz;
  float floorRadius = length(floorView - centreView);
  return uSeaRadius - floorRadius;
}

vec2 capturedWaterUv(vec3 viewPos) {
  vec4 clip = uSsrProjection * vec4(viewPos, 1.0);
  return clip.xy / clip.w * 0.5 + 0.5;
}

// Shore foam is broad enough to deserve filtering even though SSR hit testing
// deliberately uses nearest depth. A cross-shaped tent filter removes the
// half-resolution depth texture's one-texel steps without smearing the actual
// water/land rejection, which continues to use the unfiltered centre sample.
float capturedFoamWaterDepth(vec3 viewPos) {
  if (uSsrEnabled <= 0.5) return 1e6;
  vec2 uv = capturedWaterUv(viewPos);
  float depthLimit = SHORE_DEPTH_BASE + uWaveAmplitudeBase * SHORE_DEPTH_WAVE_GAIN;
  float sampleLimit = depthLimit * 2.0;
  float centre = clamp(capturedWaterDepthAtUv(uv), -sampleLimit, sampleLimit);
  float left = clamp(capturedWaterDepthAtUv(uv - vec2(uSsrTexelSize.x, 0.0)), -sampleLimit, sampleLimit);
  float right = clamp(capturedWaterDepthAtUv(uv + vec2(uSsrTexelSize.x, 0.0)), -sampleLimit, sampleLimit);
  float down = clamp(capturedWaterDepthAtUv(uv - vec2(0.0, uSsrTexelSize.y)), -sampleLimit, sampleLimit);
  float upSample = clamp(capturedWaterDepthAtUv(uv + vec2(0.0, uSsrTexelSize.y)), -sampleLimit, sampleLimit);
  return (centre * 4.0 + left + right + down + upSample) * 0.125;
}

float capturedDryTerrainAtUv(vec2 uv) {
  if (uSsrEnabled <= 0.5) return 0.0;
  if (uv.x <= 0.0 || uv.x >= 1.0 || uv.y <= 0.0 || uv.y >= 1.0) return 0.0;
  return texture2D(uSsrColor, uv).a;
}

// Screen-space distance to the actual dry waterline. Bathymetric depth alone
// inherits the square edges of terrain LOD chunks; requiring nearby dry land
// prevents an otherwise shallow chunk from becoming a rectangular foam sheet.
// Three sparse rings are enough because the depth capture is already half-res.
float capturedLandCross(vec2 uv, float radius) {
  vec2 offsetX = vec2(uSsrTexelSize.x * radius, 0.0);
  vec2 offsetY = vec2(0.0, uSsrTexelSize.y * radius);
  return max(
    max(capturedDryTerrainAtUv(uv - offsetX), capturedDryTerrainAtUv(uv + offsetX)),
    max(capturedDryTerrainAtUv(uv - offsetY), capturedDryTerrainAtUv(uv + offsetY))
  );
}

float capturedShoreProximity(vec3 viewPos) {
  vec2 uv = capturedWaterUv(viewPos);
  // Broad, progressively weaker rings make the wash reach well beyond the
  // breaking crest. The previous last sample at fourteen capture texels made
  // the foam itself end on an unmistakable screen-space contour.
  float proximity = capturedLandCross(uv, 2.0);
  proximity = max(proximity, capturedLandCross(uv, 7.0) * 0.78);
  proximity = max(proximity, capturedLandCross(uv, 16.0) * 0.48);
  proximity = max(proximity, capturedLandCross(uv, 32.0) * 0.24);
  proximity = max(proximity, capturedLandCross(uv, 54.0) * 0.08);
  return proximity;
}

// The local SSR pass intentionally stops at nearby opaque geometry. A ring is
// millions of metres away and geometrically just an annulus, so reflecting it
// analytically is both cheaper and more correct than extending the SSR depth
// range until it loses all useful precision.
bool reflectedPlanetRing(
  vec3 origin,
  vec3 rayDir,
  vec3 surfaceUp,
  out vec3 ringColour,
  out float ringAlpha
) {
  ringColour = vec3(0.0);
  ringAlpha = 0.0;
  if (uRingOpacity <= 0.0 || uRingOuterRadius <= uRingInnerRadius) return false;

  vec3 ringNormal = normalize(uRingNormal);
  float planeDenominator = dot(rayDir, ringNormal);
  if (abs(planeDenominator) < 1e-5) return false;
  float hitDistance = dot(uPlanetCentre - origin, ringNormal) / planeDenominator;
  if (hitDistance <= 0.0) return false;

  vec3 ringPoint = origin + rayDir * hitDistance;
  vec3 planetToRing = ringPoint - uPlanetCentre;
  float hitRadius = length(planetToRing - ringNormal * dot(planetToRing, ringNormal));
  float radial = (hitRadius - uRingInnerRadius)
    / max(uRingOuterRadius - uRingInnerRadius, 1e-5);
  if (radial <= 0.0 || radial >= 1.0) return false;

  float aa = max(fwidth(radial) * 1.5, 0.00035);
  float broad;
  float density = ringDensityProfile(radial, aa, broad);
  if (density < 0.004) return false;

  float viewCos = max(abs(planeDenominator), 0.08);
  ringAlpha = 1.0 - exp(-uRingOpacity * density / viewCos);

  vec3 L = normalize(uSunDirection);
  float signedSunFacing = dot(ringNormal, L);
  float signedViewFacing = dot(ringNormal, -rayDir);
  float particleScatter = 0.22 + 0.78 * sqrt(abs(signedSunFacing));
  float seesSunlitFace = step(0.0, signedSunFacing * signedViewFacing);
  float transmittedFace = mix(0.42, 0.16, clamp(density, 0.0, 1.0));
  float faceIllumination = mix(transmittedFace, 1.0, seesSunlitFace);

  float axisDistance = length(planetToRing - L * dot(planetToRing, L));
  float penumbra = max(uSeaRadius * 0.015, fwidth(axisDistance) * 1.5);
  float behindPlanet = dot(planetToRing, L) < 0.0 ? 1.0 : 0.0;
  float planetShadow = behindPlanet
    * (1.0 - smoothstep(uSeaRadius - penumbra, uSeaRadius + penumbra, axisDistance));
  float directLight = particleScatter * faceIllumination
    * min(uSunIntensity, 2.0) * 0.50;
  float illumination = 0.012 + directLight * mix(1.0, 0.025, planetShadow);
  float warmBand = 0.5 + 0.5 * sin(2.0 * PI * (radial * 3.0 + 0.18));
  vec3 bandTint = mix(vec3(0.76, 0.68, 0.57), vec3(1.08, 1.0, 0.86), warmBand);
  ringColour = uRingColor * bandTint * (0.62 + broad * 0.38)
    * uSunColor * illumination;

  // Foreground air owns the horizon. A Kasten-like curved airmass proxy is
  // enough here because the reflected ray starts at sea level; the direct
  // ring shader performs the more general full segment integration.
  float elevation = max(dot(rayDir, surfaceUp), 0.0);
  float airMass = 1.0 / max(elevation + 0.025 * exp(-11.0 * elevation), 0.025);
  vec3 extinction = exp(-uRingAtmosphereTau * airMass);
  float atmosphericVisibility = smoothstep(
    0.035,
    0.32,
    dot(extinction, vec3(0.2126, 0.7152, 0.0722))
  );
  ringColour *= extinction;
  ringAlpha *= atmosphericVisibility;

  // The reflected sightline exits through the authored cloud deck. Sample the
  // same planet-fixed global weather field as CloudLayer, so a cloud group
  // that hides the real ring also hides its reflection instead of the two
  // representations disagreeing.
  if (uCloudCoverage > 0.0 && uCloudRadius > uSeaRadius) {
    vec3 localOrigin = origin - uPlanetCentre;
    float b = dot(localOrigin, rayDir);
    float c = dot(localOrigin, localOrigin) - uCloudRadius * uCloudRadius;
    float discriminant = b * b - c;
    if (discriminant > 0.0) {
      float cloudDistance = -b + sqrt(discriminant);
      if (cloudDistance > 0.0 && cloudDistance < hitDistance) {
        vec3 cloudDirection = normalize(localOrigin + rayDir * cloudDistance);
        vec3 fixedY = normalize(cross(uWaveAxisY, uWaveAxisX));
        vec3 fixedCloudDirection = vec3(
          dot(cloudDirection, uWaveAxisX),
          dot(cloudDirection, fixedY),
          dot(cloudDirection, uWaveAxisY)
        );
        float cloudDetail;
        float cloudDensity = cloudDensityAt(
          fixedCloudDirection,
          uTime,
          uCloudDrift,
          uCloudCoverage,
          0.0,
          cloudDetail
        );
        float cloudAirmass = 1.0 / max(elevation, 0.08);
        float cloudOcclusion = 1.0 - exp(-cloudDensity * cloudAirmass * 3.2);
        ringAlpha *= 1.0 - cloudOcclusion;
      }
    }
  }

  return ringAlpha > 0.001;
}

// --- Terrain self-shadow (cascaded) -----------------------------------------
${GLSL_SHADOW_CASCADE}

void main() {
  // The world cameras overlap for opaque depth precision, but this surface is
  // alpha blended. Accept it in exactly one pass or reflected sunlight is
  // composited repeatedly into bright horizontal strips at the handovers.
  float rasterDepth = -(viewMatrix * vec4(vRenderPosition, 1.0)).z;
  if (rasterDepth < uPassMin || rasterDepth >= uPassMax) discard;

  vec3 viewRay = normalize(vRenderPosition - cameraPosition);
  vec3 V = -viewRay;
  vec3 L = normalize(uSunDirection);
  vec3 rayOrigin = cameraPosition - uPlanetCentre;
  float seaDistance = seaSphereDistance(rayOrigin, viewRay);
  vec3 seaPoint = seaDistance > 0.0
    ? rayOrigin + viewRay * seaDistance
    : normalize(vPlanetPos) * uSeaRadius;
  vec3 up = normalize(seaPoint);

  // The base normal is the exact radial direction at this fragment, not the
  // interpolated vertex normal. A sphere's normal has a closed form, and using
  // it removes the tessellation entirely — which matters here more than
  // anywhere else, because water is the smoothest surface in the scene and its
  // specular lobe turns any faceting into a hard lattice drawn across the whole
  // ocean. Wave perturbation, which is genuinely per-vertex, is added back on
  // top and is zero from orbit anyway.
  // Reconstruct the optical normal per pixel. Interpolated radial-grid normals
  // expose each far triangle as a kilometre-wide reflective wedge.
  vec3 tangent;
  vec3 bitangent;
  planetWaveBasis(up, tangent, bitangent);
  vec2 waveUv = vec2(dot(seaPoint, uWaveAxisX), dot(seaPoint, uWaveAxisY));
  // Noise used by glitter, whitecaps and shore break belongs to the rotating
  // planet as well. Sampling raw inertial vPlanetPos made the shoreline sweep
  // through a stationary noise volume at the body's full rotational speed.
  vec3 waveAxisZ = normalize(cross(uWaveAxisY, uWaveAxisX));
  vec3 planetFixedCoord = vec3(
    dot(seaPoint, uWaveAxisX),
    dot(seaPoint, waveAxisZ),
    dot(seaPoint, uWaveAxisY)
  );
  float opticalLod = 1.0 - smoothstep(6000.0, 26000.0, vDistance);
  vec2 opticalSlope = vec2(0.0);
  float opticalHeight = 0.0;
  float opticalBreaking = 0.0;
  float wh;
  vec2 ws;
  float wb;
  if (uQuality > 0) {
    opticalWave(waveUv, uWaveAmplitude * 0.52, uWaveScale, uWindAngle, 0.0, wh, ws, wb);
    opticalHeight += wh; opticalSlope += ws; opticalBreaking = max(opticalBreaking, wb);
    if (uQuality > 1) {
      opticalWave(waveUv, uWaveAmplitude * 0.24, uWaveScale * 0.53, uWindAngle + 0.14, 2.1, wh, ws, wb);
      opticalHeight += wh; opticalSlope += ws; opticalBreaking = max(opticalBreaking, wb);
      opticalWave(waveUv, uWaveAmplitude * 0.14, uWaveScale * 0.29, uWindAngle - 0.21, 4.4, wh, ws, wb);
      opticalHeight += wh; opticalSlope += ws; opticalBreaking = max(opticalBreaking, wb);
    }
    if (uQuality > 2) {
      opticalWave(waveUv, uWaveAmplitude * 0.07, uWaveScale * 0.16, uWindAngle + 0.29, 1.3, wh, ws, wb);
      opticalHeight += wh; opticalSlope += ws; opticalBreaking = max(opticalBreaking, wb);
    }
  }
  opticalHeight *= opticalLod;
  opticalSlope *= opticalLod;
  opticalBreaking *= opticalLod;
  vec3 N = normalize(
    up
      - tangent * opticalSlope.x * 0.72
      - bitangent * opticalSlope.y * 0.72
  );

  // Shadows the direct sun terms only (diffuse body colour, the two specular
  // highlights below) — same convention TerrainMaterial uses: ambient/sky
  // terms keep lighting shadowed water rather than reading as dead black.
  float selfShadow = terrainSelfShadow(vRenderPosition, N, L);

  // Cloud shadow: does the ray from this point to the star cross the cloud
  // deck? Same technique TerrainMaterial uses (ray-sphere against the deck,
  // one cloudDensityAt lookup at the crossing, no march) and the same
  // uCloudRadius/uCloudCoverage/uCloudDrift this file already carries for the
  // reflected ring's cloud occlusion below — reused here rather than adding
  // new uniforms. seaPoint is already relative to the planet centre, so the
  // factored quadratic (len-r)(len+r) stays cancellation-free at planetary
  // radii exactly like TerrainMaterial's version.
  //
  // Gated on uQuality >= 2, the same tier that already turns SSR on: the
  // fbm sum inside cloudDensityAt costs about as much as one more octave of
  // that march, and low/medium exist specifically to stay cheap.
  float cloudShadow = 1.0;
  if (uQuality >= 2 && uCloudCoverage > 0.0 && uCloudRadius > uSeaRadius) {
    float pLen = length(seaPoint);
    float b = dot(seaPoint, L);
    float c = (pLen - uCloudRadius) * (pLen + uCloudRadius);
    float discriminant = b * b - c;
    if (discriminant > 0.0) {
      float cloudDistance = -b + sqrt(discriminant);
      if (cloudDistance > 0.0) {
        vec3 cloudDirection = normalize(seaPoint + L * cloudDistance);
        vec3 fixedCloudDirection = vec3(
          dot(cloudDirection, uWaveAxisX),
          dot(cloudDirection, waveAxisZ),
          dot(cloudDirection, uWaveAxisY)
        );
        float cloudDetail;
        float cloudDensity = cloudDensityAt(
          fixedCloudDirection, uTime, uCloudDrift, uCloudCoverage, 0.0, cloudDetail
        );
        cloudShadow = 1.0 - cloudDensity * CLOUD_SHADOW_STRENGTH;
      }
    }
  }
  float directShadow = selfShadow * cloudShadow;

  // Camera altitude relative to the sea sphere decides two things: the Fresnel
  // facing convention below, and whether SSR applies at all (underwater you
  // look out through the surface, not at a mirror of it).
  float cameraAltitude = length(cameraPosition - uPlanetCentre) - uSeaRadius;
  bool underwater = cameraAltitude < 0.0;

  // Screen-space reflection: the view ray reflected off the perturbed normal,
  // marched against the capture's depth.
  bool ssrHit = false;
  vec3 ssrColor = vec3(0.0);
  float ssrConfidence = 0.0;
  vec3 viewPos = (uSsrViewMatrix * vec4(vRenderPosition, 1.0)).xyz;
  float shorelineCoverage = 1.0 - capturedDryTerrainAtUv(capturedWaterUv(viewPos));
  // Cross-partition depth cannot hide a displaced transparent triangle from
  // opaque dry terrain. Reject it using the capture's signed floor depth before
  // reflection or foam can turn that triangle into a conspicuous moving fan.
  if (uSsrEnabled > 0.5 && shorelineCoverage <= 0.001) discard;
  if (!underwater) {
    vec3 normalView = normalize(mat3(uSsrViewMatrix) * N);
    ssrHit = ssrRayMarch(viewPos, normalView, ssrColor, ssrConfidence);
  }

  // Fresnel: water is nearly a mirror at grazing angles and mostly absorptive
  // looking straight down. This single term does most of the work of making
  // water look like water. Below the surface the viewing direction reverses
  // relative to the outward normal, so the sign is removed and total internal
  // reflection takes over: straight up the underside is nearly transparent,
  // sliding to a mirror as the view approaches horizontal.
  float viewDot = dot(N, V);
  float facing = underwater ? abs(viewDot) : max(viewDot, 0.0);
  float fresnel = FRESNEL_NORMAL + FRESNEL_GRAZING * pow(1.0 - facing, FRESNEL_POWER);

  // Water emits nothing, so every term below has to go out with the sun.
  //
  // uSunIntensity is set from the body's distance to the star, not from the
  // hour, so it is still 3.4 at Halcyon's midnight. Multiplying by it without
  // this gate is what lit the sea at night: at grazing angles the Fresnel term
  // goes to 1 and the reflection below becomes essentially
  // skyColour * 3.4 * 0.5, so the ocean rendered as a bright pale sheet against
  // correctly black land.
  float sunUp = dot(up, L);
  float daylight = smoothstep(DAYLIGHT_START, DAYLIGHT_END, sunUp);

  // Body colour: depth-tinted, brightened where waves crest. Contrast raised
  // from the original 0.35 so the swell's shape actually reads in the flat
  // colour, not just in specular highlights that only catch it near the sun.
  vec3 body = mix(uDeepColor, uShallowColor, clamp(opticalHeight * BODY_COLOR_WAVE_CONTRAST + 0.5, 0.0, 1.0));
  float diffuse = max(dot(N, L), 0.0);
  vec3 color = body * uSunColor * uSunIntensity * (BODY_AMBIENT_FLOOR * daylight + diffuse * BODY_DIFFUSE_WEIGHT * directShadow);

  // Sky reflection. A single flat colour (the old behaviour) reads as a matte
  // tinted mirror, not as sky — real reflected sky is brighter and hazier
  // near the horizon than at the zenith, and glows warm near the sun even off
  // the exact specular direction. This is still not a reflection of the
  // actual scene (that needs a real reflection render pass, a bigger change
  // than this shader), but a graded, sun-aware sky reads far more like a
  // reflective surface than one flat swatch does.
  vec3 R = reflect(-V, N);
  float skyElevation = clamp(dot(R, up), 0.0, 1.0);
  vec3 skyHorizon = mix(uSkyColor, SKY_HORIZON_TINT, SKY_HORIZON_MIX);
  vec3 skyZenith = uSkyColor * SKY_ZENITH_DARKEN;
  vec3 skyReflection = mix(skyHorizon, skyZenith, skyElevation);
  float sunGlow = pow(max(dot(R, L), 0.0), SUN_GLOW_POWER);
  skyReflection += uSunColor * sunGlow * SUN_GLOW_STRENGTH;
  float reflectionIlluminance = min(uSunIntensity, 1.5);
  color = mix(
    color,
    skyReflection * reflectionIlluminance * SKY_REFLECTION_INTENSITY * daylight,
    fresnel * SKY_REFLECTION_FRESNEL_GAIN
  );

  vec3 reflectedRingColour;
  float reflectedRingAlpha;
  if (reflectedPlanetRing(
    vRenderPosition,
    R,
    up,
    reflectedRingColour,
    reflectedRingAlpha
  )) {
    // The perturbed optical normal already broadens and distorts this image
    // across the waves. Fresnel controls its strength just like every other
    // reflected source; nearby opaque SSR hits are composited later and remain
    // free to replace it.
    float ringReflectionStrength = clamp(fresnel * reflectedRingAlpha * 0.88, 0.0, 1.0);
    color = mix(color, reflectedRingColour, ringReflectionStrength);
  }

  // Multi-point glitter: many small twinkling highlights scattered across the
  // whole visible water, from perturbing the specular direction by a fine
  // per-fragment noise field rather than relying on one soft sun-aligned
  // blob. This is most of what actually reads as "water" at a glance, more
  // than the single highlight below does on its own — a real sea glitters
  // across its whole sunlit surface, not just at one spot.
  //
  // Screen-space-derivative faded the same way TerrainMaterial fades its
  // finest octave (see that file's 'footprint' comment): without this, the
  // fine noise itself would alias into exactly the kind of streaking the
  // Toksvig fix above exists to remove, just from a new source.
  // Glitter coordinate anchored to the planet surface, NOT to the camera.
  // vRenderPosition lives in the camera-relative render frame (uPlanetCentre
  // is 'planet centre relative to the camera'), so sampling noise with it
  // made the whole glitter field slide across the sea as the camera moved —
  // exactly the "noise follows camera position, not rotation" report. The
  // displaced point minus the planet centre is metric (varies across the
  // pixel, so near-field grazing still gets texture) and fixed to the
  // surface, so the twinkle stays pinned to the water like real glints do.
  vec3 sparkleCoord = planetFixedCoord * SPARKLE_COORD_SCALE
    + vec3(0.0, 0.0, uTime * SPARKLE_TIME_SCROLL);
  float sparkleFootprint = max(length(dFdx(sparkleCoord)), length(dFdy(sparkleCoord)));
  float sparkleFade = clamp(1.0 / max(sparkleFootprint * SPARKLE_FOOTPRINT_SCALE, SPARKLE_FOOTPRINT_MIN) - 1.0, 0.0, 1.0);
  vec3 jitter = (vec3(
    fbm(sparkleCoord, 3),
    fbm(sparkleCoord + SPARKLE_NOISE_OFFSET, 3),
    0.0
  ) - 0.5) * SPARKLE_JITTER_STRENGTH * sparkleFade;
  vec3 Nj = normalize(N + jitter);

  // Specular sun glitter, anti-aliased against normal undersampling.
  //
  // N's wave-tilt component is interpolated from a normal computed once per
  // vertex, and the local patch's triangles grow with distance from the
  // camera (see buildRadialGrid's exponential ring spacing) — so a pixel far
  // from the camera can straddle a triangle whose two corner normals already
  // differ by more than the glitter lobe's own angular width. A fixed, tight
  // exponent then blinks the highlight on and off block by block, aligned
  // with the mesh's own triangulation — reported back as diagonal streaks
  // across the water. Toksvig's normal-variance trick widens (and dims) the
  // lobe wherever the screen-space derivative of N says the true surface
  // detail is finer than this pixel can resolve, which is a property of the
  // pixel rather than a fixed distance cutoff, so it degrades gracefully
  // instead of needing a per-body tuned cutoff.
  float shininess = SPECULAR_SHININESS;
  vec3 dNdx = dFdx(N);
  vec3 dNdy = dFdy(N);
  float normalVariance = dot(dNdx, dNdx) + dot(dNdy, dNdy);
  float toksvig = 1.0 / (1.0 + shininess * normalVariance * TOKSVIG_VARIANCE_GAIN);
  float aaShininess = 1.0 + (shininess - 1.0) * toksvig;

  vec3 H = normalize(V + L);
  float spec = pow(max(dot(N, H), 0.0), aaShininess) * toksvig;
  color += uSunColor * uSunIntensity * spec * SPECULAR_STRENGTH * step(0.0, dot(up, L)) * directShadow;

  // The glitter field itself: many small highlights from the jittered normal
  // above, moderate exponent (not the primary spec's SPECULAR_SHININESS —
  // this is meant to scatter, not spike), faded out with
  // distance/undersampling by the same 'sparkleFade' that already zeroes the
  // jitter there.
  float sparkleSpec = pow(max(dot(Nj, H), 0.0), SPARKLE_SHININESS);
  color += uSunColor * uSunIntensity * sparkleSpec * SPARKLE_STRENGTH * sparkleFade * daylight * step(0.0, dot(up, L)) * directShadow;

  // Open-water whitecaps are sparse patches in sufficiently rough seas, not
  // every crest from every directional train. Masking the strongest breaking
  // signal with broad, planet-anchored weather noise removes the crossed-line
  // lattice while retaining occasional white water in storms.
  float whitecapDistanceFade = 1.0 - smoothstep(
    WHITECAP_DISTANCE_START,
    WHITECAP_DISTANCE_END,
    vDistance
  );
  float seaStateStrength = smoothstep(0.8, 3.5, uWaveAmplitudeBase);
  float whitecapNoise = fbm(
    planetFixedCoord * WHITECAP_NOISE_SCALE
      + vec3(uTime * 0.012, 0.0, uTime * 0.007),
    3
  );
  float crestFoam = smoothstep(0.58, 0.9, opticalBreaking)
    * smoothstep(0.62, 0.84, whitecapNoise)
    * seaStateStrength
    * whitecapDistanceFade;

  // Shore break: the opaque capture tells us how far the bottom lies below the
  // sea surface at this pixel. Larger authored waves break in deeper water and
  // therefore produce a wider foam band. Broad noise breaks up the line without
  // turning it into high-frequency static.
  float foamWaterDepth = capturedFoamWaterDepth(viewPos);
  float shoreDepth = SHORE_DEPTH_BASE + uWaveAmplitudeBase * SHORE_DEPTH_WAVE_GAIN;
  float shoreDepthTransition = clamp(
    fwidth(foamWaterDepth) * 1.5,
    0.5,
    shoreDepth * 0.45
  );
  // Spend most of the bathymetric range fading out, instead of painting the
  // entire shallow shelf white and only softening its final texel.
  float shallow = shorelineCoverage
    * (1.0 - smoothstep(
      max(SHORE_DEPTH_MIN, shoreDepth * 0.28 - shoreDepthTransition),
      shoreDepth + shoreDepthTransition,
      foamWaterDepth
    ));
  float coastProximity = foamWaterDepth < shoreDepth * 1.6
    ? capturedShoreProximity(viewPos)
    : 0.0;
  shallow *= coastProximity;
  float shoreNoise = fbm(
    planetFixedCoord * SHORE_NOISE_SCALE
      + vec3(uTime * 0.018, 0.0, uTime * 0.009),
    3
  );
  float shorePulse = smoothstep(0.28, 0.72, shoreNoise);
  float shoreSeaState = smoothstep(0.08, 2.5, uWaveAmplitudeBase);
  // Foam belongs primarily to advancing crests. A low residual wash keeps a
  // soft wake behind each breaker, but no longer fills the whole coastal shelf.
  float shoreWaveHeight = opticalHeight / max(uWaveAmplitude, 0.08);
  float shoreCrest = smoothstep(-0.02, 0.58, shoreWaveHeight);
  float shoreBreaker = mix(0.16, 1.0, shoreCrest);
  // Fade before the depth capture ends. At low altitude the physical horizon
  // is closer than this range, so no termination line can enter the view; from
  // higher up it still dissolves gradually instead of snapping at rawDepth=1.
  float shoreDistanceFade = 1.0 - smoothstep(
    uSsrFar * SHORE_CAPTURE_FADE_START,
    uSsrFar * SHORE_CAPTURE_FADE_END,
    vDistance
  );
  float shoreFoam = shallow
    * shoreBreaker
    * mix(0.5, 1.0, shorePulse)
    * mix(0.6, 1.0, shoreSeaState)
    * shoreDistanceFade;
  float foam = max(crestFoam, shoreFoam);
  color = mix(color, FOAM_COLOR * uSunIntensity * FOAM_INTENSITY * daylight, foam * FOAM_BLEND_STRENGTH);

  // Transparency. Above water the surface is effectively opaque, easing to a
  // hair of see-through straight down so shallow sea floor reads. Below it,
  // opacity follows the same Fresnel curve that drives reflection, so the
  // underside is transparent looking up and a mirror near grazing, and the
  // colour darkens with how deep the camera is.
  float opacity = underwater
    ? mix(UNDERWATER_OPACITY_NADIR, UNDERWATER_OPACITY_GRAZING, fresnel)
    : mix(SURFACE_OPACITY_NADIR, 1.0, fresnel);
  // A sub-pixel bathymetric transition replaces the binary triangle/terrain
  // intersection at the coast. Deep water remains unchanged; dry land is fully
  // rejected above and the last few centimetres fade like a wet shoreline.
  if (!underwater && uSsrEnabled > 0.5) opacity *= shorelineCoverage;
  if (underwater) {
    float depth = -cameraAltitude;
    float attenuation = exp(-depth * UNDERWATER_ABSORPTION);
    color *= mix(UNDERWATER_COLOR_FLOOR, 1.0, attenuation);
  }
  // Dithered like the terrain rather than faded by opacity: the water is
  // already alpha blended over the sea floor, so dimming it would wash the
  // whole surface out instead of revealing the impostor behind it.
  if (ditheredOut(uSurfaceFade, gl_FragCoord.xy)) discard;

  gl_FragColor = vec4(color, opacity);

  // See TerrainMaterial.
  #include <tonemapping_fragment>
  #include <colorspace_fragment>

  // Screen-space reflections, applied AFTER tone mapping and output encoding:
  // the capture already went through those (the terrain material tone-maps
  // itself), so running them again here would double-flatten the reflection.
  // The procedural sky reflection above remains the fallback on a miss; a hit
  // replaces it, weighted by the same Fresnel that drives the whole reflection
  // term, so grazing water is a near-mirror and calm water barely reflects.
  if (ssrHit && !underwater) {
    float localSsr = 1.0 - smoothstep(2500.0, 12000.0, vDistance);
    float s = clamp(fresnel * SSR_REFLECTION_STRENGTH * ssrConfidence * localSsr, 0.0, 1.0);
    gl_FragColor.rgb = mix(gl_FragColor.rgb, ssrColor, s);
  }
}
`;

/**
 * Wave height and wavelength for a body, from its own gravity and weather.
 *
 * Every ocean in the system used to be 1.6 m of swell on a 220 m scale, which
 * made a low-gravity world with a screaming atmosphere look exactly like a calm
 * high-gravity one. All three inputs the sea state actually depends on were
 * already in the body definitions.
 *
 * For a fully developed wind sea, Pierson-Moskowitz gives significant wave
 * height H = 0.21 U^2 / g, and the deep-water peak wavelength works out at
 * about 8.2 U^2 / g through T = 2*pi*U / (0.877 g). Both scale as U^2/g, so the
 * same ratio drives amplitude and scale and the waves keep their proportions
 * instead of turning into ripples or slabs.
 *
 * Fetch is not an authored field, but ocean coverage stands in for it honestly:
 * a world that is mostly water gives a wind long uninterrupted reaches to build
 * a sea over, and an archipelago world does not. The exponent is mild because
 * fetch enters wave growth weakly once a sea approaches fully developed.
 *
 * Clamped at both ends. The relation is unbounded in U^2/g, and a gas-giant
 * moon with a thin howling atmosphere would otherwise generate kilometre swells
 * that dwarf the terrain and punch through the handover to the orbital sphere.
 */
/** Deep-water period in real simulation seconds for a wavelength in metres. */
export function deepWaterWavePeriod(gravity: number, wavelength: number): number {
  const g = Math.max(0.05, gravity);
  const k = (Math.PI * 2) / Math.max(1, wavelength);
  return (Math.PI * 2) / Math.sqrt(g * k);
}

export function seaStateFor(body: CelestialBody): { amplitude: number; scale: number } {
  const wind = body.def.atmosphere?.windSpeed ?? 0;
  const gravity = Math.max(0.05, body.def.surfaceGravity);
  const coverage = body.def.terrain ? clamp01(body.def.terrain.oceanCoverage) : 0.5;

  const ratio = (wind * wind) / gravity;
  const fetch = 0.55 + Math.pow(coverage, 0.6) * 0.65;

  return {
    // Pierson-Moskowitz yields significant wave height; the shader needs
    // centre-to-crest amplitude, approximately half of that value.
    amplitude: clamp(0.105 * ratio * fetch, 0.08, 7),
    scale: clamp(8.2 * ratio * fetch, 40, 900),
  };
}

export class OceanRenderer {
  readonly root = new THREE.Group();
  private localMesh: THREE.Mesh;
  private orbitalMesh: THREE.Mesh;
  private material: THREE.ShaderMaterial;
  readonly seaRadius: number;

  /**
   * The material's uniform object is `Record<string, IUniform>`, so the
   * cascade helpers need it narrowed once here rather than a cast at every
   * call site.
   */
  private get shadowUniforms(): ShadowCascadeUniforms {
    return this.material.uniforms as unknown as ShadowCascadeUniforms;
  }

  /** Altitude above which the sphere representation takes over, metres. */
  handoverAltitude = 28000;
  /** Fully-developed wave height for this body, metres. See seaStateFor. */
  private readonly waveAmplitude: number;
  /** Screen-space reflection capture, null when the quality profile disables it. */
  private ssr: ScreenSpaceReflection | null = null;

  constructor(body: CelestialBody, seaLevelRadius: number, quality: number) {
    this.seaRadius = seaLevelRadius;
    const sea = seaStateFor(body);
    this.waveAmplitude = sea.amplitude;
    const atmosphere = body.atmosphere;
    const atmosphereScaleHeight = Math.max(MIN_SCALE_HEIGHT, atmosphere.scaleHeight);
    const mieScaleHeight = Math.max(
      MIN_SCALE_HEIGHT,
      atmosphereScaleHeight * MIE_SCALE_HEIGHT_FRACTION,
    );
    const densityScale = atmosphere.surfaceDensity > 0
      ? Math.min(
          DENSITY_SCALE_MAX,
          Math.max(DENSITY_SCALE_MIN, atmosphere.surfaceDensity / DENSITY_SCALE_NOMINAL),
        )
      : 0;
    const mieVerticalTau = atmosphere.mie * mieScaleHeight * 1.1;
    const ringAtmosphereTau = new THREE.Vector3(
      (atmosphere.rayleigh[0] * atmosphereScaleHeight + mieVerticalTau) * densityScale,
      (atmosphere.rayleigh[1] * atmosphereScaleHeight + mieVerticalTau) * densityScale,
      (atmosphere.rayleigh[2] * atmosphereScaleHeight + mieVerticalTau) * densityScale,
    );
    const cloudThickness = Math.max(1800, Math.min(6500, atmosphere.cloudAltitude * 0.9));
    const waterline = seaLevelRadius - body.radius;
    const cloudCentreAltitude = Math.max(500, atmosphere.cloudAltitude, waterline + 900);
    const cloudRadius = body.radius + cloudCentreAltitude + cloudThickness * 0.5;
    const rings = body.def.rings;

    this.material = new THREE.ShaderMaterial({
      uniforms: {
        uPlanetCentre: { value: new THREE.Vector3() },
        uSeaRadius: { value: seaLevelRadius },
        uTime: { value: 0 },
        uWaveAmplitude: { value: quality > 0 ? sea.amplitude : 0 },
        uWaveAmplitudeBase: { value: sea.amplitude },
        uWaveScale: { value: sea.scale },
        uSurfaceGravity: { value: Math.max(0.05, body.def.surfaceGravity) },
        // Fixed per-body swell direction, standing in for a prevailing wind
        // direction the body definitions don't otherwise carry. Hashed from
        // the body id (the same "one deterministic lookup, no position
        // dependence" pattern TerrainPipeline's dune wind angle uses) so
        // every run picks the same direction rather than an arbitrary one.
        uWindAngle: { value: toUnit(hashString(body.id + ':swell')) * Math.PI * 2 },
        uWaveAxisX: { value: new THREE.Vector3(1, 0, 0) },
        uWaveAxisY: { value: new THREE.Vector3(0, 0, 1) },
        uQuality: { value: Math.max(0, Math.min(MAX_WAVE_OCTAVES, quality)) },
        uPassMin: { value: 0 },
        uPassMax: { value: 1e30 },
        uRingNormal: { value: new THREE.Vector3(body.spinAxis.x, body.spinAxis.y, body.spinAxis.z) },
        uRingInnerRadius: { value: rings?.innerRadius ?? 0 },
        uRingOuterRadius: { value: rings?.outerRadius ?? 0 },
        uRingOpacity: { value: rings?.opacity ?? 0 },
        uRingColor: {
          value: rings
            ? new THREE.Color(rings.color[0], rings.color[1], rings.color[2])
            : new THREE.Color(0, 0, 0),
        },
        uRingAtmosphereTau: { value: ringAtmosphereTau },
        uCloudCoverage: { value: atmosphere.cloudCoverage },
        uCloudRadius: { value: cloudRadius },
        uCloudDrift: { value: atmosphere.windSpeed / Math.max(1, body.radius) },
        uSurfaceFade: { value: 1 },
        ...createShadowCascadeUniforms(),
        uSsrEnabled: { value: 0 },
        uSsrColor: { value: null },
        uSsrDepth: { value: null },
        uSsrProjection: { value: new THREE.Matrix4() },
        uSsrProjectionInverse: { value: new THREE.Matrix4() },
        uSsrViewMatrix: { value: new THREE.Matrix4() },
        uSsrFar: { value: 120000 },
        uSsrTexelSize: { value: new THREE.Vector2(1, 1) },
        uSunDirection: { value: new THREE.Vector3(1, 0, 0) },
        uSunColor: { value: new THREE.Color(...SUN_COLOR) },
        uSunIntensity: { value: SUN_INTENSITY_DEFAULT },
        uShallowColor: {
          value: new THREE.Color(
            body.def.palette.shallowWater[0],
            body.def.palette.shallowWater[1],
            body.def.palette.shallowWater[2],
          ),
        },
        uDeepColor: {
          value: new THREE.Color(
            body.def.palette.deepWater[0],
            body.def.palette.deepWater[1],
            body.def.palette.deepWater[2],
          ),
        },
        uSkyColor: {
          value: body.def.atmosphere
            ? new THREE.Color(
                body.def.atmosphere.hazeColor[0],
                body.def.atmosphere.hazeColor[1],
                body.def.atmosphere.hazeColor[2],
              )
            : new THREE.Color(0.02, 0.03, 0.05),
        },
      },
      vertexShader: VERTEX,
      fragmentShader: FRAGMENT,
      side: THREE.DoubleSide,
      transparent: true,
      depthWrite: false,
    });

    // The local patch has to reach the true horizon at every altitude below
    // the handover, not just near the ground. Horizon distance grows with
    // sqrt(altitude), so a radius sized for eye-height only becomes visibly
    // too small well before the handover altitude — on an Earth-sized world
    // the horizon at 28 km up is already ~600 km away, six times a flat
    // 90 km patch. Sized from the actual geometry (line-of-sight distance to
    // the horizon at the handover altitude) plus margin, so it scales with
    // both the body's radius and `handoverAltitude` instead of one constant
    // that only happened to be enough for whichever body it was tuned on.
    const horizonAtHandover = Math.sqrt(
      this.handoverAltitude * (this.handoverAltitude + 2 * seaLevelRadius),
    );
    const localRadius = horizonAtHandover * 1.25;
    this.localMesh = new THREE.Mesh(
      buildRadialGrid(quality > 1 ? 120 : 64, quality > 1 ? 320 : 160, localRadius),
      this.material,
    );
    this.localMesh.frustumCulled = false;
    this.localMesh.visible = false;
    this.localMesh.onBeforeRender = (_renderer, _scene, camera) => {
      this.material.uniforms.uPassMin.value = Number(camera.userData.transparentPassMin ?? 0);
      this.material.uniforms.uPassMax.value = Number(camera.userData.transparentPassMax ?? 1e30);
    };
    this.root.add(this.localMesh);

    // Chord sag, and why the segment count and the radius are both adjusted.
    //
    // A polygonal sphere's faces lie *inside* the sphere its vertices are on.
    // The sag in the middle of a face is about R(pi/N)^2/2, which for a seven
    // thousand kilometre ocean at 128 segments is over two kilometres — deeper
    // than the shallow sea floor. The water surface then dips below the sea bed
    // in the middle of every quad and the terrain pokes through, producing a
    // regular grid of holes across the whole ocean that looks like a texture
    // artefact and is really geometry.
    //
    // Two fixes together: enough segments that the sag is small, and a radius
    // scaled so the faces, not the vertices, sit on the true sphere.
    const segments = quality > 1 ? 512 : 256;
    const sagCompensation = 1 / Math.cos(Math.PI / segments);
    this.orbitalMesh = new THREE.Mesh(
      new THREE.SphereGeometry(seaLevelRadius * sagCompensation, segments, segments / 2),
      this.material,
    );
    this.orbitalMesh.frustumCulled = false;
    this.orbitalMesh.visible = false;
    this.orbitalMesh.onBeforeRender = this.localMesh.onBeforeRender;
    this.root.add(this.orbitalMesh);
  }

  /**
   * @param cameraPositionInertial camera position in the body's inertial frame
   * @param planetCentreRender planet centre relative to the camera
   */
  update(
    cameraPositionInertial: Vec3,
    planetCentreRender: Vec3,
    altitude: number,
    time: number,
    fixedToInertial?: Quat,
  ): void {
    const u = this.material.uniforms;
    u.uPlanetCentre.value.set(planetCentreRender.x, planetCentreRender.y, planetCentreRender.z);
    u.uTime.value = time;
    if (fixedToInertial) {
      const ringNormal = fixedToInertial.axisY().normalize();
      u.uRingNormal.value.set(ringNormal.x, ringNormal.y, ringNormal.z);
      // All moving surface fields consume this same rotating body frame. Keep
      // it current even when the orbital ocean representation is active.
      const referenceX = fixedToInertial.axisX().normalize();
      const referenceZ = fixedToInertial.axisZ().normalize();
      u.uWaveAxisX.value.set(referenceX.x, referenceX.y, referenceX.z);
      u.uWaveAxisY.value.set(referenceZ.x, referenceZ.y, referenceZ.z);
    }

    const useLocal = altitude < this.handoverAltitude;
    this.localMesh.visible = useLocal;
    this.orbitalMesh.visible = !useLocal;

    // Fade wave amplitude out across the handover band so the switch is
    // invisible: by the time the sphere takes over, waves contribute nothing.
    const fade = Math.max(0, 1 - altitude / this.handoverAltitude);
    u.uWaveAmplitude.value =
      (u.uQuality.value as number) > 0 ? this.waveAmplitude * fade * fade : 0;

    if (useLocal) {
      // Orient the local patch so its plane is tangent to the sphere beneath the
      // camera, then let the vertex shader project it onto the sea sphere.
      const up = cameraPositionInertial.normalized();
      const centre = Vec3.addVectors(planetCentreRender, up.clone().mulScalar(this.seaRadius));
      this.localMesh.position.set(centre.x, centre.y, centre.z);
      const q = new THREE.Quaternion().setFromUnitVectors(
        new THREE.Vector3(0, 1, 0),
        new THREE.Vector3(up.x, up.y, up.z),
      );
      this.localMesh.quaternion.copy(q);

      // Pass two body-fixed axes rotated into inertial space without projecting
      // them beneath the camera. The shader projects them independently at each
      // sea point. Reprojecting here made the phase chart follow the observer,
      // so travelling sideways visibly dragged the wave pattern along.
      if (!fixedToInertial) {
        u.uWaveAxisX.value.set(1, 0, 0);
        u.uWaveAxisY.value.set(0, 0, 1);
      }
    } else {
      this.orbitalMesh.position.set(planetCentreRender.x, planetCentreRender.y, planetCentreRender.z);
    }

    this.updateSsrUniforms(altitude);
  }

  /**
   * Feeds this frame's capture into the shader. Runs a frame late (the capture
   * pass happens inside `renderer.render()`, after this), which is invisible —
   * the reflection is one frame of terrain either way.
   */
  private updateSsrUniforms(altitude: number): void {
    const u = this.material.uniforms;
    if (!this.ssr) {
      u.uSsrEnabled.value = 0;
      return;
    }
    const enabled = altitude < SSR_MAX_ALTITUDE_M && (u.uQuality.value as number) >= 2;
    u.uSsrEnabled.value = enabled ? 1 : 0;
    if (!enabled) return;
    u.uSsrColor.value = this.ssr.target.texture;
    u.uSsrDepth.value = this.ssr.target.depthTexture;
    u.uSsrProjection.value.copy(this.ssr.camera.projectionMatrix);
    u.uSsrProjectionInverse.value.copy(this.ssr.camera.projectionMatrixInverse);
    u.uSsrViewMatrix.value.copy(this.ssr.camera.matrixWorldInverse);
    u.uSsrFar.value = this.ssr.camera.far;
    u.uSsrTexelSize.value.set(
      1 / Math.max(1, this.ssr.target.width),
      1 / Math.max(1, this.ssr.target.height),
    );
  }

  setWireframe(enabled: boolean): void {
    this.material.wireframe = enabled;
  }

  /**
   * Binds the screen-space reflection capture. Null disables SSR (low/medium
   * quality, or no ocean-quality tier for it).
   */
  setSsrReflection(ssr: ScreenSpaceReflection | null): void {
    this.ssr = ssr;
    (this.material.uniforms.uSsrEnabled.value as number) = ssr ? 1 : 0;
  }

  setLighting(sunDirection: Vec3, sunColor: THREE.Color, intensity: number): void {
    const u = this.material.uniforms;
    u.uSunDirection.value.set(sunDirection.x, sunDirection.y, sunDirection.z);
    u.uSunColor.value.copy(sunColor);
    u.uSunIntensity.value = intensity;
  }

  /** Cross-fade with the impostor, dithered exactly like the terrain. */
  setSurfaceFade(fade: number): void {
    this.material.uniforms.uSurfaceFade.value = fade;
  }

  /** Same cascades, same lookup, as the terrain surface — see `setShadowCascadeUniforms`. */
  setSelfShadow(cascades: readonly ShadowCascade[]): void {
    setShadowCascadeUniforms(this.shadowUniforms, cascades);
  }

  /** See LayeredRenderer.adaptShadowDistance for why this exists as a separate, continuous fade. */
  setShadowFade(fade: number): void {
    setShadowFadeUniform(this.shadowUniforms, fade);
  }

  /**
   * Detaches as well as frees. `CloudLayer.dispose` already did both; this one
   * only freed, and that asymmetry is what left a dead sea hanging in the sky.
   *
   * Disposing a geometry does not stop Three drawing the mesh — the attribute
   * arrays are still there, so the next frame simply re-uploads them. A
   * disposed-but-attached `orbitalMesh` is a planet-sized sphere at sea-level
   * radius that keeps rendering at whatever camera-relative offset it last
   * had, and once `Game.pruneAbandonedBodies` has dropped this renderer from
   * its map there is nothing left that can position or remove it. Leaving the
   * meshes parented was the difference between "freed" and "gone".
   */
  dispose(): void {
    this.root.remove(this.localMesh);
    this.root.remove(this.orbitalMesh);
    this.root.removeFromParent();
    this.localMesh.geometry.dispose();
    this.orbitalMesh.geometry.dispose();
    this.material.dispose();
  }
}

/**
 * Radial grid in the XZ plane with exponentially growing ring spacing, so
 * vertex density is highest near the viewer where waves are actually resolvable.
 *
 * WINDING. The grid lies in the XZ plane and is oriented with +Y pointing away
 * from the planet, so every triangle must wind counter-clockwise as seen from
 * +Y or the whole patch is back-facing and culled. Going round a ring in the
 * direction of increasing angle — (cos t, 0, sin t) — traverses it *clockwise*
 * from that viewpoint, because the cross product of two such radii points along
 * -Y. Both loops below therefore emit their triangles in the order that reverses
 * that. The original code did not, and the result was a local ocean that
 * vanished when viewed from above while the orbital sphere behind it looked
 * fine, so the sea appeared correct from orbit and missing near the ground.
 */
export function buildRadialGrid(
  rings: number,
  sectors: number,
  maxRadius: number,
): THREE.BufferGeometry {
  const vertexCount = rings * sectors + 1;
  const positions = new Float32Array(vertexCount * 3);
  const indices: number[] = [];

  // Centre vertex.
  positions[0] = 0;
  positions[1] = 0;
  positions[2] = 0;

  for (let r = 0; r < rings; r++) {
    const t = (r + 1) / rings;
    const radius = maxRadius * Math.pow(t, 3.1);
    for (let s = 0; s < sectors; s++) {
      const angle = (s / sectors) * Math.PI * 2;
      const i = 1 + r * sectors + s;
      positions[i * 3] = Math.cos(angle) * radius;
      positions[i * 3 + 1] = 0;
      positions[i * 3 + 2] = Math.sin(angle) * radius;
    }
  }

  // Inner fan.
  for (let s = 0; s < sectors; s++) {
    const a = 1 + s;
    const b = 1 + ((s + 1) % sectors);
    indices.push(0, b, a);
  }

  // Rings.
  for (let r = 0; r < rings - 1; r++) {
    for (let s = 0; s < sectors; s++) {
      const s1 = (s + 1) % sectors;
      const a = 1 + r * sectors + s;
      const b = 1 + r * sectors + s1;
      const c = 1 + (r + 1) * sectors + s;
      const d = 1 + (r + 1) * sectors + s1;
      indices.push(a, b, c, b, d, c);
    }
  }

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  geometry.setIndex(indices);
  geometry.computeVertexNormals();
  geometry.boundingSphere = new THREE.Sphere(new THREE.Vector3(), maxRadius * 1.05);
  return geometry;
}
